'use client';

import { useTranslations } from 'next-intl';

import { Image, Typography } from '@/components/atoms';
import { HeroBanner, LikeButton } from '@/components/molecules';
import { FavouriteType } from '@/lib/contexts/favouriting';
import { getRandomDefaultImage } from '@/lib/utils/getRandomImage';
import { cn } from '@/lib/utils/tailwind';

type HeaderBannerProps = {
  id?: number;
  title: string;
  contentType: string;
  media: string;
  content: string;
  className?: string;
  isLikeable?: boolean;
};

const HeaderBanner = ({
  id,
  title,
  contentType,
  media,
  content,
  className,
  isLikeable = false,
}: HeaderBannerProps) => {
  const t = useTranslations();
  const contentTypeTranslated =
    contentType !== undefined
      ? t(`CONTENT_TYPE_${contentType.toUpperCase()}`)
      : '';
  const image = media !== '' ? media : getRandomDefaultImage('video');
  return (
    <HeroBanner
      as={'header'}
      media={
        <>
          <Image
            alt={title}
            src={image}
            className="object-cover rounded-none"
            wrapperClassName="rounded-none"
            priority
          />
          <div
            className="absolute inset-0 bg-gradient-hero pointer-events-none"
            aria-hidden
          />
        </>
      }
      className={cn(
        'relative',
        'aspect-[4/5] sm:aspect-video sm:max-h-[85vh] sm:max-w-[1440px]',
        'sm:mx-auto',
        'overflow-hidden',
        'bg-[var(--hero-gradient)]',
        className,
      )}
    >
      <div className="[align-items:var(--hero-content-item-alignment)] [text-align:var(--hero-content-text-alignment)] flex flex-col-reverse z-10 px-6 py-8 md:px-12 md:py-8 lg:px-24 lg:py-8 xl:px-36 xl:py-10">
        {isLikeable && id && (
          <LikeButton
            contentId={id}
            contentType={contentType as keyof typeof FavouriteType}
            className="w-fit min-w-36 sm:min-w-48 mt-(--vertical-spacing-3)"
            defaultContent={t('FOCAL_LIKE')}
            likedContent={t('FOCAL_LIKED')}
            variant={
              contentType === 'Article' || contentType === 'Collection'
                ? 'secondary'
                : 'ghost'
            }
          />
        )}
        {contentType !== 'Video' && content && (
          <div>
            <Typography.Text
              variant={'paragraph'}
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'px-0.5 md:px-2',
                'py-1',
                'w-fit',
                'hero-text-style',
                'bg-(--hero-text-background)',
                'text-shadow-(--hero-text-shadow)',
                'text-(--hero-text-color)',
              ])}
            >
              {content}
            </Typography.Text>
          </div>
        )}
        {title && (
          <div>
            <Typography.Heading
              variant={'h4'}
              as="h1"
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'px-0.5 md:px-2',
                'py-1',
                'w-fit',
                'bg-(--hero-text-background)',
                'text-(--hero-text-color)',
                'text-shadow-(--hero-text-shadow)',
              ])}
            >
              {title}
            </Typography.Heading>
          </div>
        )}
        {contentTypeTranslated && (
          <div>
            <Typography.Text
              variant={'body'}
              className={cn([
                'hero-text-display',
                'hero-box-decoration',
                'px-0.5 md:px-2',
                'py-1',
                'w-fit',
                'hero-text-style',
                'bg-(--hero-text-background)',
                'text-shadow-(--hero-text-shadow)',
                'text-(--hero-text-color)',
              ])}
            >
              {contentTypeTranslated}
            </Typography.Text>
          </div>
        )}
      </div>
    </HeroBanner>
  );
};

export { HeaderBanner };
